/**
 * Theme utilities — light/dark switching.
 *
 * The vendored commons CSS keys dark styles off BOTH `[data-theme="dark"]` and
 * the `.dark` class, and Tailwind's `dark:` variant (darkMode: 'class') keys off
 * `.dark`. We set both on <html> so every layer agrees. Persisted in localStorage
 * and initialised from the OS preference on first load.
 */

import { isMockMode } from '@/config/runtime'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'taxhive-theme'

export function getStoredTheme(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'light' || v === 'dark' ? v : null
  } catch {
    return null
  }
}

export function getSystemTheme(): Theme {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

export function getInitialTheme(): Theme {
  return getStoredTheme() ?? getSystemTheme()
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  root.classList.toggle('dark', theme === 'dark')
  root.setAttribute('data-theme', theme)
  root.style.colorScheme = theme
}

export function setTheme(theme: Theme): void {
  applyTheme(theme)
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    /* storage unavailable (private mode) — apply only */
  }
}

/** Call once at app start, before React renders, to avoid a flash. */
export function initTheme(): void {
  applyTheme(getInitialTheme())
}

/* ------------------------------------------------------------------ *
 * Accent colour theme (orange / indigo / blue). Palettes live in
 * src/styles/color-themes.css, selected via `data-accent` on <html>.
 * ------------------------------------------------------------------ */

export type Accent = 'orange' | 'indigo' | 'blue'

export const ACCENTS: { id: Accent; label: string; swatch: string }[] = [
  { id: 'orange', label: 'Orange', swatch: '#f97316' },
  { id: 'indigo', label: 'Dark Blue', swatch: '#4f46e5' },
  { id: 'blue', label: 'Classic Blue', swatch: '#0a84ff' },
]

const ACCENT_KEY = 'taxhive-accent'
const DEFAULT_ACCENT: Accent = 'orange'

export function getStoredAccent(): Accent | null {
  try {
    const v = localStorage.getItem(ACCENT_KEY)
    return v === 'orange' || v === 'indigo' || v === 'blue' ? v : null
  } catch {
    return null
  }
}

export function getInitialAccent(): Accent {
  return getStoredAccent() ?? DEFAULT_ACCENT
}

export function applyAccent(accent: Accent): void {
  document.documentElement.setAttribute('data-accent', accent)
}

export function setAccent(accent: Accent): void {
  try {
    localStorage.setItem(ACCENT_KEY, accent)
  } catch {
    /* storage unavailable — apply only */
  }
  // Honour mock-mode's forced grey; otherwise apply the chosen accent now.
  if (!isMockMode()) applyAccent(accent)
}

/**
 * Call once at app start, before React renders. In MOCK data mode the whole UI
 * is forced to a neutral grey accent (a clear "this isn't real data" signal);
 * live mode uses the user's chosen accent (orange by default).
 */
export function initAccent(): void {
  if (isMockMode()) {
    document.documentElement.setAttribute('data-accent', 'grey')
  } else {
    applyAccent(getInitialAccent())
  }
}
